import React, { useCallback, useEffect } from 'react'

import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { ConfigProvider, Layout, Spin } from 'antd'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router'

import { useSSEEvent } from '@/context/SSEContext'
import { useLayoutConfigContext } from '@/providers/layoutConfig.provider'
import { queries } from '@/queries'
import { API, COMMONS } from '@/utils'

const { Content } = Layout

const Minimal = (props) => {
	const { children } = props
	const [searchParams] = useSearchParams()

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
	const queryClient = useQueryClient()

	const layoutContext = useLayoutConfigContext()
	const { logo, favicon, storePic, publicSettings } = layoutContext

	useEffect(() => {
		let link = document.querySelector("link[rel~='icon']")

		if (!link) {
			link = document.createElement('link')
			link.rel = 'icon'
			document.getElementsByTagName('head')[0].appendChild(link)
		}

		if (favicon) {
			link.href = API.SETTINGS_UPLOADS_URL + favicon
		}
	}, [favicon])

	const faviconHandler = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queries.settings.favicon.queryKey,
		})
	}, [])

	const logoHandler = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: queries.settings.logo.queryKey })
	}, [])

	const storePicHandler = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queries.settings.storePic.queryKey,
		})
	}, [])

	const systemSettingsHandler = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: [API.QUERY_KEY_PUBLIC_SETTINGS],
		})
	}, [])

	useSSEEvent(API.SSE_FAVICON, faviconHandler)
	useSSEEvent(API.SSE_LOGO, logoHandler)
	useSSEEvent(API.SSE_STORE_PIC, storePicHandler)
	useSSEEvent(API.SSE_SYSTEM_SETTING, systemSettingsHandler)

	const childrenWithProps = React.Children.map(children, (element) =>
		React.cloneElement(element, {
			logo,
			storePic,
			publicSettings,
		}),
	)

	const isLiffRoute = window.location.pathname.startsWith('/liff')

	useEffect(() => {
		if (isLiffRoute) {
			const root = document.documentElement
			const originalSize = root.style.fontSize
			root.style.fontSize = '24px'
			root.classList.add('liff-mode')

			return () => {
				root.style.fontSize = originalSize
				root.classList.remove('liff-mode')
			}
		}
	}, [isLiffRoute])

	return (
		<>
			<Helmet>
				{publicSettings?.TITLE?.valueString ? (
					<title>{publicSettings?.TITLE?.valueString}</title>
				) : (
					''
				)}
			</Helmet>
			<ConfigProvider
				theme={{
					token: {
						colorPrimary: publicSettings?.PRIMARY_COLOR?.valueString,
						colorLink: publicSettings?.PRIMARY_COLOR?.valueString,
						colorLinkActive: publicSettings?.PRIMARY_COLOR?.valueString,
						colorLinkHover: publicSettings?.PRIMARY_COLOR?.valueString,
						...(isLiffRoute && {
							fontSize: 24,
							controlHeight: 60,
						}),
					},
				}}
			>
				<div className='flex flex-col w-full min-h-full'>
					<Spin
						spinning={
							useIsFetching({
								fetchStatus: 'fetching',
							}) > 0
						}
						style={{
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 1001,
						}}
					>
						<Layout className='min-h-screen'>
							<Content className='bg-white'>{childrenWithProps}</Content>
						</Layout>
					</Spin>
				</div>
			</ConfigProvider>
		</>
	)
}

export default Minimal
